import {
	getCurrentScope,
	onScopeDispose,
	readonly,
	type Ref,
	ref,
} from 'vue';
import { callSafe } from '../utils/functions';

interface UseMobileReturn {
	isMobile: Ref<boolean>;
	isMobileStrict: Ref<boolean>;
	stop: () => void;
}

export type IsMobileWatchCallback = (isMobile: boolean) => void;

export type IsMobileType = 'normal' | 'strict';

interface IsMobileState {
	isMobile: boolean;
	isMobileStrict: boolean;
	mobileMediaQuery: MediaQueryList;
	strictLandscape: MediaQueryList;
	strictPortrait: MediaQueryList;
	watchers: Record<IsMobileType, IsMobileWatchCallback[]>;
}

const STRICT_LANDSCAPE = 'only screen and (pointer: coarse) and (max-height: 767px) and (orientation: landscape)';
const STRICT_PORTRAIT = 'only screen and (pointer: coarse) and (max-width: 767px) and (orientation: portrait)';
const STRICT_LANDSCAPE_NX = 'only screen and (pointer: coarse) and (max-height: calc(767px / :ratio)) and (orientation: landscape)';
const STRICT_PORTRAIT_NX = 'only screen and (pointer: coarse) and (max-width: calc(767px / :ratio)) and (orientation: portrait)';

let state: IsMobileState | null = null;

function evaluateStrict(): boolean {
	if (window.devicePixelRatio === 1) {
		return (
			window.matchMedia(STRICT_LANDSCAPE).matches
			|| window.matchMedia(STRICT_PORTRAIT).matches
		);
	}

	const ratio = window.devicePixelRatio.toString();
	return (
		window.matchMedia(STRICT_LANDSCAPE_NX.replace(
			/:ratio/,
			ratio,
		)).matches
		|| window.matchMedia(STRICT_PORTRAIT_NX.replace(
			/:ratio/,
			ratio,
		)).matches
	);
}

function ensureInitialized(): IsMobileState {
	if (state) {
		return state;
	}

	const mobileMediaQuery = window.matchMedia('only screen and (max-width: 767px)');
	const strictLandscape = window.matchMedia(STRICT_LANDSCAPE);
	const strictPortrait = window.matchMedia(STRICT_PORTRAIT);

	const initialState: IsMobileState = {
		isMobile: mobileMediaQuery.matches,
		isMobileStrict: evaluateStrict(),
		mobileMediaQuery,
		strictLandscape,
		strictPortrait,
		watchers: {
			normal: [],
			strict: [],
		},
	};

	function notify(type: IsMobileType): void {
		if (type === 'normal') {
			const next = mobileMediaQuery.matches;
			initialState.isMobile = next;

			for (const watcher of initialState.watchers.normal) {
				callSafe(() => watcher(next));
			}
		} else {
			const next = evaluateStrict();
			initialState.isMobileStrict = next;

			for (const watcher of initialState.watchers.strict) {
				callSafe(() => watcher(next));
			}
		}
	}

	mobileMediaQuery.addEventListener(
		'change',
		() => notify('normal'),
	);
	strictLandscape.addEventListener(
		'change',
		() => notify('strict'),
	);
	strictPortrait.addEventListener(
		'change',
		() => notify('strict'),
	);

	state = initialState;
	return state;
}

export function getIsMobile(): boolean {
	return ensureInitialized().isMobile;
}

export function getIsMobileStrict(): boolean {
	return ensureInitialized().isMobileStrict;
}

export function watch(
	callback: IsMobileWatchCallback,
	type: IsMobileType = 'normal',
): () => void {
	const watcherState = ensureInitialized();
	watcherState.watchers[type].push(callback);

	return () => {
		const index = watcherState.watchers[type].indexOf(callback);

		if (index > -1) {
			watcherState.watchers[type].splice(
				index,
				1,
			);
		}
	};
}

export function useMobile(): UseMobileReturn {
	const watcherState = ensureInitialized();
	const isMobileState = ref(watcherState.isMobile);
	const isMobileStrictState = ref(watcherState.isMobileStrict);
	const disposers: Array<() => void> = [
		watch((value) => {
			isMobileState.value = value;
		}),
		watch(
			(value) => {
				isMobileStrictState.value = value;
			},
			'strict',
		),
	];

	const stop = () => {
		while (disposers.length) {
			const disposer = disposers.pop();
			disposer?.();
		}
	};

	if (getCurrentScope()) {
		onScopeDispose(stop);
	}

	return {
		isMobile: readonly(isMobileState),
		isMobileStrict: readonly(isMobileStrictState),
		stop,
	};
}
